/**
 * Cache handling mirrors `useApproveAssetStep`: the response already carries the rolled-back
 * progress, so it seeds the read cache directly, and the file detail refetches because
 * unapproving a step of a completed chain sends the file back to review.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ASSET_APPROVAL_CHAIN_QUERY_KEYS } from '@/entities/asset-approval-chain';
import { PROJECT_FILE_QUERY_KEYS } from '@/entities/project-file';
import { PROJECT_FOLDER_QUERY_KEYS } from '@/entities/project-folder';
import { track } from '@/shared/analytics';

import { unapproveAssetStep } from './approval-actions.api';
import { invalidateFolderAggregates } from './invalidate-folder-aggregates';

export { unapproveAssetStep };

interface UseUnapproveAssetStepOptions {
  projectId: string;
  /** Folder currently in view — used to refresh its files list after unapproving. */
  folderId?: string;
}

interface UnapproveAssetStepVariables {
  assetId: string;
  /**
   * Step to unapprove. Omitted = the current step. On an in-order chain, naming an
   * earlier step rewinds to it, resetting it plus every step after it; on an any-order
   * chain only the named step is cleared.
   */
  stepId?: string;
}

export function useUnapproveAssetStep(options: UseUnapproveAssetStepOptions) {
  const queryClient = useQueryClient();
  const { projectId, folderId } = options;

  return useMutation({
    mutationFn: ({ assetId, stepId }: UnapproveAssetStepVariables) =>
      unapproveAssetStep(projectId, assetId, stepId),
    onSuccess: (data, { assetId }) => {
      queryClient.setQueryData(ASSET_APPROVAL_CHAIN_QUERY_KEYS.detail(projectId, assetId), data);
      void queryClient.invalidateQueries({
        queryKey: ASSET_APPROVAL_CHAIN_QUERY_KEYS.detail(projectId, assetId),
      });
      void queryClient.invalidateQueries({
        queryKey: PROJECT_FILE_QUERY_KEYS.detail(projectId, assetId),
      });
      if (folderId) {
        void queryClient.invalidateQueries({
          queryKey: PROJECT_FOLDER_QUERY_KEYS.files(projectId, folderId),
        });
      }
      invalidateFolderAggregates(queryClient, projectId, folderId);
      track('asset_step_unapproved', { project_id: projectId });
    },
  });
}
